<template>
  <div class="container-fluid p-5">
    <loading :active.sync="isLoading" :is-full-page="fullPage" />

    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <!-- Header -->
          <div class="card-header d-flex justify-content-between align-items-center">
            <span>UTM Keyword List</span>
            <button class="btn btn-primary" @click="openCreateModal">
              <i class="fas fa-plus"></i> Create UTM
            </button>
          </div>

          <!-- Body -->
          <div class="card-body">
            <div class="table-responsive mt-4">
              <table class="common-table">
                <thead>
                  <tr>
                    <th
                      v-for="(label, key) in columns"
                      :key="key"
                      @click="sortBy(key)"
                      style="cursor: pointer"
                    >
                      {{ label }}
                      <span v-if="filter.sortKey === key">
                        <i v-if="filter.orderBy === 'asc'" class="fas fa-sort-up"></i>
                        <i v-else class="fas fa-sort-down"></i>
                      </span>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="item in dataList" :key="item.id">
                    <td>{{ item.id }}</td>
                    <td>{{ item.key_word }}</td>
                    <td>{{ item.register_count }}</td>
                    <td>{{ item.login_count }}</td>
                    <td>{{ item.created_at || "-" }}</td>

                    <td>
                      <button
                        class="btn btn-option text-primary"
                        @click="openEditModal(item)"
                      >
                        <i class="fas fa-edit"></i>
                      </button>

                      <button
                        v-b-modal.bv-modal-delete
                        class="btn btn-option text-danger"
                        @click="deleteData(item)"
                      >
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>

                  <tr v-if="dataList.length === 0">
                    <td
                      :colspan="Object.keys(columns).length + 1"
                      class="text-center"
                    >
                      Empty List
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Pagination -->
              <div class="table-pagination-minimal">
                <div class="table-pagination-row">
                  <div v-if="dataList.length" class="table-info-minimal left-align">
                    Showing {{ metaData.from }} to {{ metaData.to }} of
                    {{ metaData.total }} entries
                  </div>

                  <nav class="pagination-minimal">
                    <ul>
                      <li :class="{ disabled: metaData.prev_page_url === null }">
                        <button
                          @click.prevent="next(metaData.current_page - 1)"
                          :disabled="metaData.prev_page_url === null"
                        >
                          <i class="fas fa-angle-left"></i>
                        </button>
                      </li>

                      <li
                        v-for="page in visiblePages"
                        :key="page"
                        :class="{ active: metaData.current_page === page }"
                      >
                        <button @click.prevent="next(page)">
                          {{ page }}
                        </button>
                      </li>

                      <li
                        :class="{
                          disabled: metaData.current_page === metaData.last_page,
                        }"
                      >
                        <button
                          @click.prevent="next(metaData.current_page + 1)"
                          :disabled="metaData.current_page === metaData.last_page"
                        >
                          <i class="fas fa-angle-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>

            <!-- Create / Edit Modal -->
            <b-modal
              id="bv-modal-utm"
              ref="utm-modal"
              :title="isEdit ? 'Edit UTM Keyword' : 'Create UTM Keyword'"
              hide-footer
            >
              <div class="form-group">
                <label>
                  Keyword <span class="text-danger">*</span>
                </label>
                <input
                  v-model="form.key_word"
                  type="text"
                  class="form-control"
                  placeholder="Enter keyword"
                />
              </div>

              <p class="text-danger mt-2">{{ formError }}</p>

              <div class="d-flex justify-content-end mt-3">
                <button
                  class="btn btn-secondary mr-2"
                  @click="$refs['utm-modal'].hide()"
                >
                  Cancel
                </button>
                <button class="btn btn-primary" @click="submitForm">
                  {{ isEdit ? "Update" : "Create" }}
                </button>
              </div>
            </b-modal>

            <!-- Delete Modal -->
            <b-modal
              id="bv-modal-delete"
              ref="delete-modal"
              title="Delete UTM"
              hide-footer
            >
              <p class="my-4">Are you sure you want to delete this UTM?</p>
              <p class="my-4 text-danger">{{ alert }}</p>
              <b-button class="btn-danger" block @click="deleteItem">
                Delete
              </b-button>
            </b-modal>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  name: "UtmList",
  metaInfo: {
    title: "UTM List",
    titleTemplate: "%s ← BXB",
  },
  components: { Loading },

  data() {
    return {
      isLoading: false,
      fullPage: true,
      alert: "",
      deleteId: null,

      // create/edit
      isEdit: false,
      formError: "",
      form: {
        id: null,
        key_word: "",
      },

      dataList: [],

      columns: {
        id: "ID",
        key_word: "Keyword",
        register_count: "Register Count",
        login_count: "Login Count",
        created_at: "Created At",
      },

      filter: {
        page: 1,
        per_page: 10,
        orderBy: "desc",
        sortKey: "created_at",
      },

      metaData: {
        last_page: null,
        current_page: 1,
        from: null,
        to: null,
        total: null,
        prev_page_url: null,
      },
    };
  },

  computed: {
    visiblePages() {
      const last = this.metaData.last_page;
      const current = this.metaData.current_page;
      if (!last) return [];

      const pages = [];
      for (let i = 1; i <= last; i++) {
        if (Math.abs(i - current) < 3 || i === 1 || i === last) {
          pages.push(i);
        }
      }
      return pages;
    },
  },

  methods: {
    ...mapActions({
      utmListAction: "utmListAction",
      utmDeleteAction: "utmDeleteAction",
      utmEditAction: "utmEditAction",
      utmStoreAction: "utmStoreAction",
    }),

    sortBy(key) {
      if (this.filter.sortKey === key) {
        this.filter.orderBy = this.filter.orderBy === "asc" ? "desc" : "asc";
      } else {
        this.filter.sortKey = key;
        this.filter.orderBy = "asc";
      }
      this.fetchData();
    },

    async fetchData() {
      this.isLoading = true;
      try {
        const res = await this.utmListAction(this.filter);
        if (res.success) {
          this.dataList = res.data.data;
          const meta = res.data.meta;
          this.metaData = {
            last_page: meta.lastPage,
            current_page: meta.currentPage,
            prev_page_url: meta.prevPageUrl,
            from: meta.from,
            to: meta.to,
            total: meta.total,
          };
        }
      } finally {
        this.isLoading = false;
      }
    },

    next(page) {
      this.filter.page = page;
      this.fetchData();
    },

    openCreateModal() {
      this.isEdit = false;
      this.formError = "";
      this.form = { id: null, key_word: "" };
      this.$refs["utm-modal"].show();
    },

    openEditModal(item) {
      this.isEdit = true;
      this.formError = "";
      this.form = { id: item.id, key_word: item.key_word };
      this.$refs["utm-modal"].show();
    },

    async submitForm() {
      if (!this.form.key_word) {
        this.formError = "Keyword is required";
        return;
      }

      this.isLoading = true;
      try {
        const fd = new FormData();
        fd.append("key_word", this.form.key_word);

        let res;
        if (this.isEdit) {
          fd.append("id", this.form.id);
          res = await this.utmEditAction(fd);
        } else {
          res = await this.utmStoreAction(fd);
        }

        if (!res.success) {
          this.formError = res.message;
          return;
        }

        this.$refs["utm-modal"].hide();
        this.fetchData();
      } finally {
        this.isLoading = false;
      }
    },

    deleteData(item) {
      this.deleteId = item.id;
      this.alert = "";
    },

    async deleteItem() {
      this.isLoading = true;
      try {
        const res = await this.utmDeleteAction({ id: this.deleteId });
        if (!res.success) {
          this.alert = res.message;
          return;
        }
        this.$refs["delete-modal"].hide();
        this.fetchData();
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    if (this.$route.query.page) {
      this.filter.page = Number(this.$route.query.page);
    }
    this.fetchData();
  },
};
</script>

